// UUID validation utilities

/**
 * Validates if a value is a valid UUID (v4)
 */
export const isValidUUID = (value) => {
  if (!value || typeof value !== 'string') {
    return false;
  }

  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(value);
};

/**
 * Validates an array of UUIDs
 */
export const areValidUUIDs = (uuids) => {
  if (!Array.isArray(uuids)) {
    return false;
  }

  return uuids.every(uuid => isValidUUID(uuid));
};

/**
 * Filters out invalid UUIDs from an array and logs warnings
 */
export const filterValidUUIDs = (uuids, context = 'unknown') => {
  if (!Array.isArray(uuids)) {
    console.error(`[UUID Validation] Expected array but received ${typeof uuids} in context: ${context}`);
    return [];
  }

  const validUUIDs = [];
  const invalidUUIDs = [];

  uuids.forEach(uuid => {
    if (isValidUUID(uuid)) {
      validUUIDs.push(uuid);
    } else {
      invalidUUIDs.push(uuid);
    }
  });

  if (invalidUUIDs.length > 0) {
    console.warn(`[UUID Validation] Found ${invalidUUIDs.length} invalid UUIDs in context: ${context}`, invalidUUIDs);
  }

  return validUUIDs;
};

/**
 * Validates route ID and throws descriptive error if invalid
 */
export const validateRouteId = (routeId, context = 'unknown') => {
  if (!routeId) {
    throw new Error(`Route ID is required for ${context}`);
  }

  if (!isValidUUID(routeId)) {
    throw new Error(`Invalid route ID format for ${context}: ${routeId}`);
  }

  return routeId;
};

/**
 * Validates order IDs array and throws descriptive error if invalid
 */
export const validateOrderIds = (orderIds, context = 'unknown') => {
  if (!orderIds || !Array.isArray(orderIds)) {
    throw new Error(`Order IDs array is required for ${context}`);
  }

  if (orderIds.length === 0) {
    throw new Error(`At least one order ID is required for ${context}`);
  }

  const validOrderIds = filterValidUUIDs(orderIds, context);

  if (validOrderIds.length === 0) {
    throw new Error(`No valid order IDs found for ${context}`);
  }

  if (validOrderIds.length !== orderIds.length) {
    console.warn(`[UUID Validation] Filtered ${orderIds.length - validOrderIds.length} invalid UUIDs for ${context}`);
  }

  return validOrderIds;
};

// Phone number formatting utilities

/**
 * Validates an email address.
 *
 * Stricter than the common `[^\s@]+@[^\s@]+\.[^\s@]+` pattern: it rejects
 * leading, trailing, and consecutive dots in the local part and domain
 * (e.g. "jane.@example.com"), which Shopify also rejects as "is invalid".
 * Each dot-separated segment must contain at least one non-dot character,
 * and the domain must have at least one dot.
 *
 * @param {string} email - The email address to validate
 * @returns {boolean} - True if the email is a valid format
 */
export const isValidEmail = (email) => {
  if (!email || typeof email !== 'string') {
    return false;
  }

  const emailRegex = /^[^\s@.]+(\.[^\s@.]+)*@[^\s@.]+(\.[^\s@.]+)+$/;
  return emailRegex.test(email.trim());
};

/**
 * Formats a phone number to US format: (XXX) XXX-XXXX
 * @param {string} value - The phone number input
 * @returns {string} - Formatted phone number
 */
export const formatPhoneNumber = (value) => {
  if (!value) return '';

  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '');

  // Limit to 10 digits
  const limitedDigits = digits.slice(0, 10);

  // Format based on length
  if (limitedDigits.length <= 3) {
    return limitedDigits;
  } else if (limitedDigits.length <= 6) {
    return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`;
  } else {
    return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
  }
};

/**
 * Removes formatting from a phone number to get just digits
 * @param {string} value - Formatted phone number
 * @returns {string} - Digits only
 */
export const unformatPhoneNumber = (value) => {
  if (!value) return '';
  return value.replace(/\D/g, '');
};